import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useOpenOrders, useOrderAnalysis } from '@/hooks/queries/useOrders';
import { cn } from '@/lib/utils';
import type { OpenOrder } from '@/lib/api/client';

function formatInstruction(instruction: string): string {
  switch (instruction) {
    case 'BUY_TO_OPEN': return 'BTO';
    case 'BUY_TO_CLOSE': return 'BTC';
    case 'SELL_TO_OPEN': return 'STO';
    case 'SELL_TO_CLOSE': return 'STC';
    case 'BUY': return 'Buy';
    case 'SELL': return 'Sell';
    default: return instruction;
  }
}

function formatExpDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function formatEnteredTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function getOrderDescription(order: OpenOrder): string {
  if (order.complexOrderStrategyType && order.complexOrderStrategyType !== 'NONE') {
    return order.complexOrderStrategyType.replace(/_/g, ' ');
  }
  if (order.legs.length === 1) {
    return order.legs[0].assetType === 'OPTION' ? 'Single Option' : 'Equity';
  }
  if (order.legs.length === 2) return 'Spread';
  if (order.legs.length === 4) return 'Custom 4 Leg';
  return `${order.legs.length} Leg`;
}

function getUnderlyingSymbol(order: OpenOrder): string | null {
  for (const leg of order.legs) {
    if (leg.underlyingSymbol) return leg.underlyingSymbol;
  }
  for (const leg of order.legs) {
    if (leg.assetType === 'EQUITY') return leg.symbol;
  }
  return null;
}

const difficultyConfig = {
  easy: { label: 'Likely to fill', color: 'text-green-700 bg-green-50 border-green-200', icon: '●' },
  moderate: { label: 'May need patience', color: 'text-yellow-700 bg-yellow-50 border-yellow-200', icon: '●' },
  hard: { label: 'Unlikely at this price', color: 'text-orange-700 bg-orange-50 border-orange-200', icon: '●' },
  unlikely: { label: 'Very unlikely to fill', color: 'text-red-700 bg-red-50 border-red-200', icon: '●' },
};

function FillAnalysis({ order }: { order: OpenOrder }) {
  const { data: analysis, isLoading } = useOrderAnalysis(order);

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400">
        <Spinner size="sm" />
        Analyzing fill probability...
      </div>
    );
  }

  if (!analysis) return null;

  const config = difficultyConfig[analysis.fillDifficulty];
  const perContract = order.quantity > 0 ? order.quantity : 1;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
      {/* Fill difficulty badge */}
      <div className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded border', config.color)}>
        <span className="text-[8px]">{config.icon}</span>
        {config.label}
      </div>

      {/* Price comparison */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-gray-400">Natural</div>
          <div className={cn('font-mono font-medium', analysis.naturalPrice >= 0 ? 'text-green-600' : 'text-red-600')}>
            {analysis.naturalPrice >= 0 ? '+' : ''}{(analysis.naturalPrice / (perContract * 100)).toFixed(2)}
          </div>
        </div>
        <div>
          <div className="text-gray-400">Mid</div>
          <div className={cn('font-mono font-medium', analysis.midPrice >= 0 ? 'text-green-600' : 'text-red-600')}>
            {analysis.midPrice >= 0 ? '+' : ''}{(analysis.midPrice / (perContract * 100)).toFixed(2)}
          </div>
        </div>
        {analysis.limitPrice != null && (
          <div>
            <div className="text-gray-400">Your Limit</div>
            <div className={cn('font-mono font-medium', analysis.limitPrice >= 0 ? 'text-green-600' : 'text-red-600')}>
              {analysis.limitPrice >= 0 ? '+' : ''}{(analysis.limitPrice / (perContract * 100)).toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Per-leg bid/ask */}
      {analysis.legs.length > 0 && (
        <div className="space-y-0.5">
          {analysis.legs.map((leg, i) => (
            <div key={i} className="flex items-center justify-between text-[11px] text-gray-500">
              <span>
                {leg.strikePrice && `$${leg.strikePrice}`}
                {leg.putCall === 'PUT' ? 'P' : leg.putCall === 'CALL' ? 'C' : ''}
              </span>
              <span className="font-mono">
                {leg.bid != null && leg.ask != null
                  ? `${leg.bid.toFixed(2)} / ${leg.ask.toFixed(2)}`
                  : '--'
                }
                {leg.spread != null && (
                  <span className="text-gray-400 ml-1">(${leg.spread.toFixed(2)} wide)</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Suggestions */}
      {analysis.suggestions.length > 0 && (
        <div className="space-y-1">
          {analysis.suggestions.map((suggestion, i) => (
            <p key={i} className="text-[11px] text-gray-500 leading-relaxed">
              {suggestion}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order }: { order: OpenOrder }) {
  const underlying = getUnderlyingSymbol(order);
  const hasOptions = order.legs.some(l => l.assetType === 'OPTION');
  const [showAnalysis, setShowAnalysis] = useState(false);

  return (
    <Card>
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              {underlying && (
                <Link
                  to={`/symbols/${underlying}`}
                  className="text-base font-bold text-brand-600 hover:text-brand-700"
                >
                  {underlying}
                </Link>
              )}
              <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-medium">
                {order.status}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {getOrderDescription(order)} &middot; {order.orderType}
              {order.price != null && ` @ $${order.price.toFixed(2)}`}
            </div>
          </div>
          <div className="text-right text-xs text-gray-400">
            {formatEnteredTime(order.enteredTime)}
          </div>
        </div>

        {/* Legs */}
        <div className="space-y-1">
          {order.legs.map((leg, i) => {
            const isShort = leg.instruction.includes('SELL');
            return (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'text-xs font-medium w-8',
                    isShort ? 'text-red-600' : 'text-green-600'
                  )}>
                    {formatInstruction(leg.instruction)}
                  </span>
                  {hasOptions && leg.assetType === 'OPTION' ? (
                    <span className="text-gray-900">
                      {leg.expirationDate && formatExpDate(leg.expirationDate)}
                      {' '}
                      <span className="font-medium">${leg.strikePrice}</span>
                      {' '}
                      <span className="text-gray-500">{leg.putCall === 'PUT' ? 'P' : 'C'}</span>
                    </span>
                  ) : (
                    <span className="text-gray-900">{leg.symbol}</span>
                  )}
                </div>
                <span className="text-gray-600 text-xs">
                  x{leg.quantity}
                </span>
              </div>
            );
          })}
        </div>

        {/* Order ID + Analysis toggle */}
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">Order #{order.orderId}</span>
          {hasOptions && (
            <button
              onClick={() => setShowAnalysis(!showAnalysis)}
              className="text-[11px] text-brand-600 hover:text-brand-700 font-medium"
            >
              {showAnalysis ? 'Hide analysis' : 'Fill analysis'}
            </button>
          )}
        </div>

        {/* Fill Analysis (lazy loaded on expand) */}
        {showAnalysis && <FillAnalysis order={order} />}
      </CardContent>
    </Card>
  );
}

export function OrdersPage() {
  const { data: orders, isLoading, error } = useOpenOrders();

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Open Orders</h1>
        <span className="text-sm text-gray-500">
          {orders && orders.length > 0 && `${orders.length} order${orders.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-500">Loading orders from Schwab...</span>
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="p-6 text-center text-red-600">
            Failed to load open orders. Make sure Schwab is connected in Settings.
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && orders && orders.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            No open orders found.
          </CardContent>
        </Card>
      )}

      {orders && orders.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map(order => (
            <OrderCard key={order.orderId} order={order} />
          ))}
        </div>
      )}
    </div>
  );
}
